import { DEFAULT_TIMEZONE } from '../../shared/timezone'

export const DEFAULT_TZ = DEFAULT_TIMEZONE

export type WindowOption = 'today' | 'last7days' | 'last30days' | 'all'

const MS_PER_DAY = 86_400_000

const PARTS_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>()

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = PARTS_FORMATTER_CACHE.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
    PARTS_FORMATTER_CACHE.set(tz, f)
  }
  return f
}

export function tzMidnightUtc(tz: string, now: Date): Date {
  const fmt = partsFormatter(tz)
  const partsAt = (d: Date) => {
    const ps = fmt.formatToParts(d)
    const num = (t: Intl.DateTimeFormatPartTypes) =>
      Number(ps.find(p => p.type === t)?.value)
    const h = num('hour')
    return { Y: num('year'), M: num('month'), D: num('day'), h: h === 24 ? 0 : h, m: num('minute'), s: num('second') }
  }
  const { Y, M, D } = partsAt(now)
  const targetWall = Date.UTC(Y, M - 1, D)
  // Iterate so the offset is sampled at the midnight candidate, not at `now`.
  // Converges in ≤2 iterations even across DST transitions.
  let guess = new Date(targetWall)
  for (let i = 0; i < 3; i++) {
    const g = partsAt(guess)
    const drift = Date.UTC(g.Y, g.M - 1, g.D, g.h, g.m, g.s) - targetWall
    if (drift === 0) return guess
    guess = new Date(guess.getTime() - drift)
  }
  return guess
}

export function windowStartUtc(option: WindowOption, tz: string, now: Date): Date | null {
  if (option === 'all') return null
  const midnight = tzMidnightUtc(tz, now)
  switch (option) {
    case 'today': return midnight
    case 'last7days': return new Date(midnight.getTime() - 6 * MS_PER_DAY)
    case 'last30days': return new Date(midnight.getTime() - 29 * MS_PER_DAY)
  }
}

export function unionWindowStartUtc(
  option: WindowOption,
  clinics: ReadonlyArray<{ timezone?: string }>,
  now: Date,
): Date | null {
  if (option === 'all') return null
  if (clinics.length === 0) return windowStartUtc(option, DEFAULT_TZ, now)
  let min = Number.POSITIVE_INFINITY
  for (const c of clinics) {
    const start = windowStartUtc(option, c.timezone || DEFAULT_TZ, now)
    if (start && start.getTime() < min) min = start.getTime()
  }
  return Number.isFinite(min) ? new Date(min) : windowStartUtc(option, DEFAULT_TZ, now)
}
